/**
 * Super-admin → Priority Support config
 *
 * Sets the platform-wide SLA + contact details. Tenants on the
 * `priority_support` flag see this card in their Priority Support page.
 */
import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Save, LifeBuoy } from "lucide-react";
import { GoldButton, PageHeader, Field, Input, Textarea } from "@/components/Ui";

export default function AdminPrioritySupport() {
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        try { const { data } = await api.get("/platform/priority-support"); setForm(data); }
        catch (e) { toast.error(apiError(e)); }
    }, []);
    useEffect(() => { load(); }, [load]);

    if (!form) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;

    const save = async () => {
        setSaving(true);
        try { await api.patch("/platform/priority-support", form); toast.success("Saved"); load(); }
        catch (e) { toast.error(apiError(e)); }
        finally { setSaving(false); }
    };

    return (
        <>
            <PageHeader title="Priority Support Configuration" subtitle="One platform-wide config — shown to every tenant whose plan has the `priority_support` flag enabled." action={
                <GoldButton onClick={save} disabled={saving} data-testid="save"><Save className="w-4 h-4 inline mr-1"/>{saving ? "Saving…" : "Save"}</GoldButton>
            }/>

            <div className="border border-primary/30 bg-primary/5 p-4 mb-6 flex items-start gap-2">
                <LifeBuoy className="w-4 h-4 text-primary mt-0.5 shrink-0"/>
                <div className="text-xs text-muted-foreground">Token <code className="text-primary">{`{sla_hours}`}</code> is substituted automatically inside the body markdown.</div>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
                <div className="bg-card border border-border p-5 space-y-4">
                    <div className="overline">SLA & body</div>
                    <Field label="SLA (business hours)">
                        <Input type="number" min={1} max={72} value={form.sla_hours}
                            onChange={e => setForm({...form, sla_hours: Number(e.target.value)})} data-testid="sla-hours"/>
                    </Field>
                    <Field label="Body (markdown)">
                        <Textarea rows={5} value={form.body_md || ""}
                            onChange={e => setForm({...form, body_md: e.target.value})} data-testid="body-md"/>
                    </Field>
                </div>

                <div className="bg-card border border-border p-5 space-y-4">
                    <div className="overline">Contact channels</div>
                    <Field label="Priority email">
                        <Input value={form.email || ""} onChange={e => setForm({...form, email: e.target.value})}
                            placeholder="priority-support@lawos.in" data-testid="email"/>
                    </Field>
                    <Field label="Direct phone">
                        <Input value={form.phone || ""} onChange={e => setForm({...form, phone: e.target.value})}
                            placeholder="+91 80 4711 0000" data-testid="phone"/>
                    </Field>
                    <Field label="WhatsApp number">
                        <Input value={form.whatsapp || ""} onChange={e => setForm({...form, whatsapp: e.target.value})}
                            placeholder="+91 98860 00000" data-testid="whatsapp"/>
                    </Field>
                    <Field label="Knowledge base URL (optional)">
                        <Input value={form.kb_url || ""} onChange={e => setForm({...form, kb_url: e.target.value})}
                            placeholder="https://help.lawos.in/priority" data-testid="kb-url"/>
                    </Field>
                </div>
            </div>
        </>
    );
}
